import { useTranslation } from"react-i18next";
import { useState, useEffect } from"react";
import axios from"axios";
import Layout from"@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from"@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from"@/components/ui/select";
import { Label } from"@/components/ui/label";
import { Badge } from"@/components/ui/badge";
import { DollarSign, TrendingUp, TrendingDown, AlertTriangle } from"lucide-react";
import { toast } from"sonner";

const CostDashboardPage = ({ user, onLogout }) => {
 const { t } = useTranslation();
 const [projects, setProjects] = useState([]);
 const [selectedProject, setSelectedProject] = useState("");
 const [dashboard, setDashboard] = useState(null);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 loadProjects();
 }, []);

 useEffect(() => {
 if (selectedProject) {
 loadDashboard();
 }
 }, [selectedProject]);

 const loadProjects = async () => {
 try {
 const response = await axios.get("/projects");
 setProjects(response.data);
 if (response.data.length > 0) {
 setSelectedProject(response.data[0].id);
 }
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 }
 };

 const loadDashboard = async () => {
 setLoading(true);
 try {
 const response = await axios.get(`/finance/dashboard/costs/${selectedProject}`);
 setDashboard(response.data);
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 } finally {
 setLoading(false);
 }
 };

 return (
 <Layout user={user} onLogout={onLogout}>
 <div data-testid="cost-dashboard-page"className="space-y-6">
 <div className="flex items-center justify-between">
 <div>
 <h1 className="font-display text-3xl font-bold tracking-tight uppercase text-slate-900 mb-1">{t("costDashboard.title")}</h1>
 <p className="text-slate-600">Budget vs actual cost tracking</p>
 </div>
 <div className="flex items-center gap-3">
 <Label>Project:</Label>
 <Select value={selectedProject} onValueChange={setSelectedProject}>
 <SelectTrigger className="w-64"data-testid="project-select">
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 {projects.map((project) => (
 <SelectItem key={project.id} value={project.id}>
 {project.name}
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 </div>

 {loading ? (
 <div className="flex items-center justify-center h-64">
 <div className="text-slate-600">Loading dashboard...</div>
 </div>
 ) : dashboard ? (
 <>
 {/* Summary Cards */}
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
 <Card data-testid="budget-card"className="">
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-slate-600 mb-1">Total Budget</p>
 <p className="text-2xl font-bold text-slate-900">€{dashboard.summary.total_budget.toFixed(2)}</p>
 </div>
 <DollarSign className="h-7 w-7 text-blue-600"/>
 </div>
 </CardContent>
 </Card>
 <Card data-testid="actual-card"className="">
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-slate-600 mb-1">Actual Spent</p>
 <p className="text-2xl font-bold text-slate-900">€{dashboard.summary.total_actual.toFixed(2)}</p>
 </div>
 <TrendingUp className="h-7 w-7 text-amber-600"/>
 </div>
 </CardContent>
 </Card>
 <Card data-testid="available-card"className="">
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-slate-600 mb-1">Available</p>
 <p className="text-2xl font-bold text-slate-900">€{dashboard.summary.available.toFixed(2)}</p>
 </div>
 <DollarSign className="h-7 w-7 text-emerald-600"/>
 </div>
 </CardContent>
 </Card>
 <Card data-testid="variance-card"className="">
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-slate-600 mb-1">Variance</p>
 <p className="text-2xl font-bold text-slate-900">
 {dashboard.summary.variance >= 0 ? '+' : ''}€{dashboard.summary.variance.toFixed(2)}
 </p>
 <p className="text-[11px] text-slate-500">{dashboard.summary.variance_pct.toFixed(1)}%</p>
 </div>
 {dashboard.summary.variance >= 0 ? <TrendingUp className="h-7 w-7 text-emerald-600"/> : <TrendingDown className="h-7 w-7 text-rose-600"/>}
 </div>
 </CardContent>
 </Card>
 </div>

 {/* Alerts */}
 {dashboard.summary.over_budget && (
 <Card data-testid="over-budget-alert"className="">
 <CardContent className="p-4">
 <div className="flex items-center gap-3">
 <AlertTriangle className="h-7 w-7 text-rose-600"/>
 <div>
 <p className="font-medium text-slate-900">Budget Overrun Alert</p>
 <p className="text-sm text-slate-600">Project is over budget by €{Math.abs(dashboard.summary.variance).toFixed(2)}</p>
 </div>
 </div>
 </CardContent>
 </Card>
 )}

 {/* Cost Breakdown by Cost Code */}
 <Card className="">
 <CardHeader>
 <CardTitle>Cost Breakdown by Cost Code</CardTitle>
 </CardHeader>
 <CardContent>
 <div className="overflow-x-auto">
 <table className="w-full">
 <thead className="border-b border-slate-200">
 <tr>
 <th className="text-left p-3 text-sm font-medium text-slate-600">Cost Code</th>
 <th className="text-left p-3 text-sm font-medium text-slate-600">Category</th>
 <th className="text-right p-3 text-sm font-medium text-slate-600">Budgeted</th>
 <th className="text-right p-3 text-sm font-medium text-slate-600">Actual</th>
 <th className="text-right p-3 text-sm font-medium text-slate-600">Variance</th>
 <th className="text-right p-3 text-sm font-medium text-slate-600">%</th>
 </tr>
 </thead>
 <tbody>
 {dashboard.cost_breakdown.map((item) => (
 <tr key={item.cost_code} data-testid={`cost-code-${item.cost_code}`} className="border-b border-white/40 hover:bg-white/80">
 <td className="p-3 text-sm font-medium text-slate-900">{item.cost_code}</td>
 <td className="p-3 text-sm text-slate-600">
 <Badge variant="outline"className="bg-blue-500/10 text-blue-700 border-blue-200/60 text-xs">
 {item.category}
 </Badge>
 </td>
 <td className="p-3 text-sm text-slate-900 text-right">€{item.budgeted.toFixed(2)}</td>
 <td className="p-3 text-sm text-slate-900 text-right">€{item.actual.toFixed(2)}</td>
 <td className="p-3 text-sm text-right font-medium text-slate-900">
 {item.variance >= 0 ? '+' : ''}€{item.variance.toFixed(2)}
 </td>
 <td className="p-3 text-sm text-right text-slate-600">
 {item.variance_pct.toFixed(1)}%
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 </CardContent>
 </Card>

 {/* Overdue Invoices */}
 {dashboard.overdue_invoices && dashboard.overdue_invoices.length > 0 && (
 <Card data-testid="overdue-invoices-card"className="">
 <CardHeader>
 <CardTitle className="flex items-center gap-2 text-slate-900">
 <AlertTriangle className="h-6 w-6 text-rose-600"/>
 Overdue Invoices
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="space-y-2">
 {dashboard.overdue_invoices.map((inv) => (
 <div key={inv.invoice_number} className="flex items-center justify-between p-3 bg-white rounded-sm border border-slate-200">
 <div>
 <p className="font-medium text-slate-900">{inv.invoice_number} - {inv.vendor}</p>
 <p className="text-sm text-slate-600">Due: {new Date(inv.due_date).toLocaleDateString()}</p>
 </div>
 <div className="text-right">
 <p className="font-medium text-slate-900">€{inv.amount.toFixed(2)}</p>
 <Badge className="bg-rose-500/10 text-rose-700 text-xs">{inv.days_overdue} days overdue</Badge>
 </div>
 </div>
 ))}
 </div>
 </CardContent>
 </Card>
 )}
 </>
 ) : null}
 </div>
 </Layout>
 );
};

export default CostDashboardPage;
